/**
 * Chunk-weighted KG indexing progress + ETA.
 *
 * LightRAG cost scales with document size (→ chunk count), not doc count.
 * When workspace batch progress is available (`cur_batch` / `total_batches`
 * from LightRAG's pipeline_status), we use it for early ETA before any doc
 * finishes — typically after the first extraction batch starts.
 */

import type { IDocInfo } from './metaphysics-kg';

/** ~1200 token chunks × ~4 chars/token — used until we calibrate from finished docs. */
export const defaultCharsPerChunk = 4800;

/** Bootstrap ms/chunk before we have completed docs or batch progress. */
const bootstrapMsPerChunk = 20_000;

/** EMA time constant for ms/unit velocity (~45s). */
const etaSmoothTauMs = 45_000;

/** Max relative change in displayed ETA per second (12%/s). */
const etaMaxRelativeStepPerSec = 0.12;

export interface IEtaSmoothingState {
  msPerUnitEma: number | null;
  /** Highest totalUnits seen — avoids ETA jumping up when totals revise upward. */
  totalUnitsFloor: number;
  displayedEtaMs: number | null;
  updatedAtMs: number;
}

export interface IApplyEtaSmoothingInput {
  etaMs: number | null;
  etaReady: boolean;
  completedUnits: number;
  totalUnits: number;
  elapsedMs: number | null;
  nowMs: number;
  state: IEtaSmoothingState | null;
}

/**
 * Damp ETA jitter from discrete batch ticks and shifting workload estimates.
 * Progress bars should use raw `completedUnits`; only the displayed ETA is smoothed.
 */
export function applyEtaSmoothing(input: IApplyEtaSmoothingInput): {
  etaMs: number | null;
  etaReady: boolean;
  state: IEtaSmoothingState | null;
} {
  const { etaReady, completedUnits, totalUnits, elapsedMs, nowMs, state } = input;

  if (
    !etaReady ||
    input.etaMs == null ||
    elapsedMs == null ||
    elapsedMs <= 0 ||
    completedUnits <= 0
  ) {
    return { etaMs: null, etaReady: false, state: null };
  }

  const totalUnitsFloor = Math.max(state?.totalUnitsFloor ?? 0, totalUnits);
  const remainingUnits = Math.max(0, totalUnitsFloor - completedUnits);
  const rawMsPerUnit = elapsedMs / completedUnits;

  const dtMs = state ? Math.max(1, nowMs - state.updatedAtMs) : 0;
  const alpha = state ? 1 - Math.exp(-dtMs / etaSmoothTauMs) : 1;
  const msPerUnitEma =
    state?.msPerUnitEma != null
      ? state.msPerUnitEma * (1 - alpha) + rawMsPerUnit * alpha
      : rawMsPerUnit;

  let displayedEtaMs = Math.round(msPerUnitEma * remainingUnits);

  if (state?.displayedEtaMs != null && dtMs > 0) {
    const maxStep = state.displayedEtaMs * etaMaxRelativeStepPerSec * (dtMs / 1000);
    displayedEtaMs = Math.round(
      Math.max(
        state.displayedEtaMs - maxStep,
        Math.min(state.displayedEtaMs + maxStep, displayedEtaMs),
      ),
    );
  }

  displayedEtaMs = Math.max(0, displayedEtaMs);

  return {
    etaMs: displayedEtaMs,
    etaReady: true,
    state: {
      msPerUnitEma,
      totalUnitsFloor,
      displayedEtaMs,
      updatedAtMs: nowMs,
    },
  };
}

export interface IKgWorkspaceInFlight {
  curBatch: number;
  totalBatches: number;
  jobStart?: string | null;
}

export interface IKgIndexingEtaInput {
  docs: IDocInfo[];
  elapsedMs: number | null;
  inFlight?: IKgWorkspaceInFlight | null;
  nowMs?: number;
}

export interface IKgIndexingEtaResult {
  completedUnits: number;
  totalUnits: number;
  completedDocs: number;
  totalDocs: number;
  failedDocs: number;
  /** Chunk-weighted completion 0–100. */
  pctWorkload: number;
  etaMs: number | null;
  etaReady: boolean;
  charsPerChunk: number;
}

function isTerminalSuccess(status: string): boolean {
  return status === 'processed';
}

function isTerminalFailed(status: string): boolean {
  return status === 'failed';
}

/** Calibrate chars/chunk from docs that finished with known chunk counts. */
export function calibrateCharsPerChunk(docs: IDocInfo[]): number {
  let charSum = 0;
  let chunkSum = 0;
  for (const d of docs) {
    if (!isTerminalSuccess(d.status)) continue;
    if (d.chunks == null || d.chunks <= 0) continue;
    if (d.contentLength == null || d.contentLength <= 0) continue;
    charSum += d.contentLength;
    chunkSum += d.chunks;
  }
  if (chunkSum > 0) return Math.max(500, charSum / chunkSum);
  return defaultCharsPerChunk;
}

/** Estimated extraction workload for one doc, in chunk units. */
export function estimateDocChunkUnits(doc: IDocInfo, charsPerChunk: number): number {
  if (doc.chunks != null && doc.chunks > 0) return doc.chunks;
  if (doc.contentLength != null && doc.contentLength > 0) {
    return Math.max(1, Math.ceil(doc.contentLength / charsPerChunk));
  }
  return 1;
}

export function computeKgIndexingEta(input: IKgIndexingEtaInput): IKgIndexingEtaResult {
  const { docs, elapsedMs, inFlight } = input;
  const nowMs = input.nowMs ?? Date.now();
  const charsPerChunk = calibrateCharsPerChunk(docs);

  let totalUnits = 0;
  let processedUnits = 0;
  let failedUnits = 0;
  let completedDocs = 0;
  let failedDocs = 0;

  for (const d of docs) {
    const units = estimateDocChunkUnits(d, charsPerChunk);
    totalUnits += units;
    if (isTerminalSuccess(d.status)) {
      processedUnits += units;
      completedDocs += 1;
    } else if (isTerminalFailed(d.status)) {
      failedUnits += units;
      failedDocs += 1;
    }
  }

  let completedUnits = processedUnits + failedUnits;

  // LightRAG pipeline batches ≈ extraction chunks across the workspace.
  if (inFlight && inFlight.totalBatches > 0) {
    totalUnits = Math.max(totalUnits, inFlight.totalBatches);
    const batchDone = Math.max(0, Math.min(inFlight.curBatch, inFlight.totalBatches));
    // Batch counter is authoritative for workload % once available.
    completedUnits = Math.max(completedUnits, batchDone);
  }

  // No batch signal yet: credit partial progress on the first in-flight doc.
  const hasBatchSignal = (inFlight?.totalBatches ?? 0) > 0 && (inFlight?.curBatch ?? 0) > 0;
  if (!hasBatchSignal && processedUnits === 0 && elapsedMs != null && elapsedMs > 0) {
    const active = docs.find((d) => d.status === 'processing' || d.status === 'analyzing');
    if (active) {
      const docUnits = estimateDocChunkUnits(active, charsPerChunk);
      const anchorMs = active.updatedAt ? new Date(active.updatedAt).getTime() : null;
      if (anchorMs != null && Number.isFinite(anchorMs)) {
        const docElapsed = Math.max(0, nowMs - anchorMs);
        const msPerChunk = completedUnits > 0 ? elapsedMs / completedUnits : bootstrapMsPerChunk;
        const partial = Math.min(docUnits * 0.9, docElapsed / msPerChunk);
        completedUnits = Math.max(completedUnits, partial);
      }
    }
  }

  const totalDocs = docs.length;
  const remainingUnits = Math.max(0, totalUnits - completedUnits);
  const pctWorkload =
    totalUnits > 0 ? Math.min(100, Math.round((completedUnits / totalUnits) * 100)) : 0;

  let etaMs: number | null = null;
  let etaReady = false;

  if (elapsedMs != null && elapsedMs > 0 && remainingUnits > 0 && completedUnits > 0) {
    const msPerUnit = elapsedMs / completedUnits;
    etaMs = Math.round(msPerUnit * remainingUnits);
    etaReady =
      hasBatchSignal ||
      processedUnits >= 1 ||
      completedUnits >= 0.5 ||
      (completedUnits > 0 && elapsedMs >= 8_000);
  }

  return {
    completedUnits,
    totalUnits,
    completedDocs,
    totalDocs,
    failedDocs,
    pctWorkload,
    etaMs,
    etaReady,
    charsPerChunk,
  };
}

/** Docset indexing: char-weighted units from converted markdown size. */
export function estimateDocsetItemChunkUnits(
  convertedCharCount: number | null | undefined,
  charsPerChunk: number = defaultCharsPerChunk,
): number {
  if (convertedCharCount != null && convertedCharCount > 0) {
    return Math.max(1, Math.ceil(convertedCharCount / charsPerChunk));
  }
  return 1;
}

export interface IDocsetIndexingItem {
  state: string;
  convertedCharCount: number | null;
  pushedAt: string | null;
}

export function computeDocsetIndexingEta(
  items: IDocsetIndexingItem[],
  elapsedMs: number,
  pushable: number,
  indexed: number,
  earliestPushedMs: number | null,
  nowMs: number = Date.now(),
): { etaMs: number | null; etaReady: boolean; totalUnits: number; completedUnits: number } {
  const charsPerChunk = defaultCharsPerChunk;
  let totalUnits = 0;
  let completedUnits = 0;

  for (const it of items) {
    const units = estimateDocsetItemChunkUnits(it.convertedCharCount, charsPerChunk);
    totalUnits += units;
    if (it.state === 'completed') completedUnits += units;
    else if (it.state === 'failed') completedUnits += units;
  }

  if (totalUnits === 0 && pushable > 0) {
    totalUnits = pushable;
    completedUnits = indexed;
  }

  const active = items.find((it) => it.state === 'extracting');
  if (active && completedUnits < totalUnits) {
    const units = estimateDocsetItemChunkUnits(active.convertedCharCount, charsPerChunk);
    const anchor = active.pushedAt ? new Date(active.pushedAt).getTime() : earliestPushedMs;
    if (anchor != null && Number.isFinite(anchor)) {
      const phaseElapsed = Math.max(0, nowMs - anchor);
      const msPerChunk =
        completedUnits > 0 && elapsedMs > 0 ? elapsedMs / completedUnits : bootstrapMsPerChunk;
      completedUnits = Math.max(completedUnits, Math.min(units * 0.9, phaseElapsed / msPerChunk));
    }
  }

  const remaining = Math.max(0, totalUnits - completedUnits);
  let etaMs: number | null = null;
  let etaReady = false;

  if (remaining > 0 && completedUnits > 0 && elapsedMs > 0) {
    etaMs = Math.round((elapsedMs / completedUnits) * remaining);
    etaReady = indexed >= 1 || completedUnits >= 0.5 || elapsedMs >= 8_000;
  }

  return { etaMs, etaReady, totalUnits, completedUnits };
}
